import { derived, get, readable, type Readable } from 'svelte/store';

export interface RecordSelector<TValue> {
	(key: string | undefined): Readable<TValue | undefined>;
	clearCache(): void;
	cacheSize(): number;
}

/**
 * Subscribe to one key of a record store; notify only when that entry reference changes.
 */
export function createRecordSelector<TValue>(
	store: Readable<Record<string, TValue>>,
	resolveKey: (key: string) => string = (key) => key,
): RecordSelector<TValue> {
	const cache = new Map<string, Readable<TValue | undefined>>();

	const select = ((key: string | undefined) => {
		if (!key) {
			return readable<TValue | undefined>(undefined);
		}

		const recordKey = resolveKey(key);
		let selector = cache.get(recordKey);

		if (!selector) {
			selector = {
				subscribe(run) {
					let current = get(store)[recordKey] as TValue | undefined;
					run(current);

					return store.subscribe((record) => {
						const next = record[recordKey] as TValue | undefined;
						if (next !== current) {
							current = next;
							run(current);
						}
					});
				},
			};
			cache.set(recordKey, selector);
		}

		return selector;
	}) as RecordSelector<TValue>;

	select.clearCache = () => cache.clear();
	select.cacheSize = () => cache.size;

	return select;
}

/** Derived boolean — only notifies subscribers when membership for `memberId` changes. */
export function createListMembershipSelector(
	store: Readable<string[]>,
	memberId: string,
): Readable<boolean> {
	return derived(store, ($list) => $list.indexOf(memberId) !== -1);
}

/** Derived value at a record key — only notifies when that entry reference changes. */
export function createRecordEntrySelector<TValue>(
	store: Readable<Record<string, TValue>>,
	key: string,
): Readable<TValue | undefined> {
	return derived(store, ($record) => $record[key]);
}

/** Derived boolean for record key presence. */
export function createRecordKeyExistsSelector(
	store: Readable<Record<string, unknown>>,
	key: string,
): Readable<boolean> {
	return derived(store, ($record) => typeof $record[key] !== 'undefined');
}
